import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import { Toaster } from '../../components/ui/sonner';
import { toast } from 'sonner';
import { MapPin, Mail, Lock, User, Loader2, Eye, EyeOff } from 'lucide-react';
import { trackLogin, trackCompleteRegistration } from '../../lib/metaPixel';

const LoginPage = () => {
    const { loginWithEmail, loginWithGoogle, register, isAuthenticated, error, clearError, loading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    const [activeTab, setActiveTab] = useState('login');
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Login form state
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    
    // Register form state
    const [registerName, setRegisterName] = useState('');
    const [registerEmail, setRegisterEmail] = useState('');
    const [registerPassword, setRegisterPassword] = useState('');
    const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            const from = location.state?.from?.pathname || '/dashboard';
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, navigate, location]);

    // Clear errors when switching tabs
    useEffect(() => {
        clearError();
    }, [activeTab, clearError]);

    // Get return URL for redirects after login
    const getReturnUrl = () => {
        return location.state?.from?.pathname || '/dashboard';
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        
        if (!loginEmail || !loginPassword) {
            toast.error('Please fill in all fields');
            return;
        }
        
        setIsSubmitting(true);
        try {
            await loginWithEmail(loginEmail, loginPassword);
            trackLogin('email'); // Track login in Meta Pixel
            toast.success('Welcome back!');
            navigate(getReturnUrl(), { replace: true });
        } catch (err) {
            toast.error(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        
        if (!registerName || !registerEmail || !registerPassword || !registerConfirmPassword) {
            toast.error('Please fill in all fields');
            return;
        }
        
        if (registerPassword !== registerConfirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        
        if (registerPassword.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }
        
        setIsSubmitting(true);
        try {
            await register(registerEmail, registerPassword, registerName);
            trackCompleteRegistration('email'); // Track registration in Meta Pixel
            toast.success('Account created successfully!');
            navigate(getReturnUrl(), { replace: true });
        } catch (err) {
            toast.error(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoogleLogin = () => {
        // Save the return URL for Google OAuth callback
        const returnUrl = getReturnUrl();
        if (returnUrl !== '/dashboard') {
            localStorage.setItem('loginReturnUrl', returnUrl);
        }
        trackLogin('google'); // Track Google login attempt
        loginWithGoogle();
    };

    return (
        <div className="min-h-screen flex">
            {/* Left Panel - Form */}
            <div className="flex-1 flex items-center justify-center p-8 bg-white">
                <div className="w-full max-w-md">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2 mb-8" data-testid="login-logo">
                        <div className="w-10 h-10 rounded-lg bg-primary-600 flex items-center justify-center">
                            <MapPin className="w-6 h-6 text-white" />
                        </div>
                        <span className="font-heading font-bold text-2xl text-neutral-900">
                            RecruitNearMe
                        </span>
                    </Link>

                    {/* Show message if redirected from another page */}
                    {location.state?.message && (
                        <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-xl text-primary-800 text-sm">
                            {location.state.message}
                        </div>
                    )}

                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                        <TabsList className="grid w-full grid-cols-2 mb-8">
                            <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                            <TabsTrigger value="register" data-testid="register-tab">Create Account</TabsTrigger>
                        </TabsList>

                        {/* Login Tab */}
                        <TabsContent value="login">
                            <Card className="border-0 shadow-none">
                                <CardHeader className="px-0 pt-0">
                                    <CardTitle className="text-2xl">Welcome back</CardTitle>
                                    <CardDescription>
                                        Sign in to your account to continue
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="px-0 space-y-6">
                                    {/* Google Login */}
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="w-full h-12"
                                        onClick={handleGoogleLogin}
                                        data-testid="google-login-btn"
                                    >
                                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                            <path
                                                fill="#4285F4"
                                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                            />
                                            <path
                                                fill="#34A853"
                                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                            />
                                            <path
                                                fill="#FBBC05"
                                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                            />
                                            <path
                                                fill="#EA4335"
                                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                            />
                                        </svg>
                                        Continue with Google
                                    </Button>

                                    <div className="relative">
                                        <div className="absolute inset-0 flex items-center">
                                            <Separator className="w-full" />
                                        </div>
                                        <div className="relative flex justify-center text-xs uppercase">
                                            <span className="bg-white px-2 text-muted-foreground">
                                                Or continue with email
                                            </span>
                                        </div>
                                    </div>

                                    {/* Email Login Form */}
                                    <form onSubmit={handleLogin} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="login-email">Email</Label>
                                            <div className="relative">
                                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                                                <Input
                                                    id="login-email"
                                                    type="email"
                                                    placeholder="you@example.com"
                                                    value={loginEmail}
                                                    onChange={(e) => setLoginEmail(e.target.value)}
                                                    className="pl-10 h-12"
                                                    data-testid="login-email-input"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="login-password">Password</Label>
                                            <div className="relative">
                                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                                                <Input
                                                    id="login-password"
                                                    type={showPassword ? 'text' : 'password'}
                                                    placeholder="Enter your password"
                                                    value={loginPassword}
                                                    onChange={(e) => setLoginPassword(e.target.value)}
                                                    className="pl-10 pr-10 h-12"
                                                    data-testid="login-password-input"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                                                >
                                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                                </button>
                                            </div>
                                        </div>
                                        <Button
                                            type="submit"
                                            className="w-full h-12 bg-primary-600 hover:bg-primary-700"
                                            disabled={isSubmitting}
                                            data-testid="login-submit-btn"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                    Signing in...
                                                </>
                                            ) : (
                                                'Sign In'
                                            )}
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Register Tab */}
                        <TabsContent value="register">
                            <Card className="border-0 shadow-none">
                                <CardHeader className="px-0 pt-0">
                                    <CardTitle className="text-2xl">Create an account</CardTitle>
                                    <CardDescription>
                                        Get started with your job search journey
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="px-0 space-y-6">
                                    {/* Google Register */}
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="w-full h-12"
                                        onClick={handleGoogleLogin}
                                        data-testid="google-register-btn"
                                    >
                                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                            <path
                                                fill="#4285F4"
                                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                            />
                                            <path
                                                fill="#34A853"
                                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                            />
                                            <path
                                                fill="#FBBC05"
                                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                            />
                                            <path
                                                fill="#EA4335"
                                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                            />
                                        </svg>
                                        Sign up with Google
                                    </Button>

                                    <div className="relative">
                                        <div className="absolute inset-0 flex items-center">
                                            <Separator className="w-full" />
                                        </div>
                                        <div className="relative flex justify-center text-xs uppercase">
                                            <span className="bg-white px-2 text-muted-foreground">
                                                Or register with email
                                            </span>
                                        </div>
                                    </div>

                                    {/* Email Register Form */}
                                    <form onSubmit={handleRegister} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="register-name">Full Name</Label>
                                            <div className="relative">
                                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                                                <Input
                                                    id="register-name"
                                                    type="text"
                                                    placeholder="John Doe"
                                                    value={registerName}
                                                    onChange={(e) => setRegisterName(e.target.value)}
                                                    className="pl-10 h-12"
                                                    data-testid="register-name-input"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="register-email">Email</Label>
                                            <div className="relative">
                                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                                                <Input
                                                    id="register-email"
                                                    type="email"
                                                    placeholder="you@example.com"
                                                    value={registerEmail}
                                                    onChange={(e) => setRegisterEmail(e.target.value)}
                                                    className="pl-10 h-12"
                                                    data-testid="register-email-input"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="register-password">Password</Label>
                                            <div className="relative">
                                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                                                <Input
                                                    id="register-password"
                                                    type={showPassword ? 'text' : 'password'}
                                                    placeholder="Min. 8 characters"
                                                    value={registerPassword}
                                                    onChange={(e) => setRegisterPassword(e.target.value)}
                                                    className="pl-10 pr-10 h-12"
                                                    data-testid="register-password-input"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                                                >
                                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="register-confirm-password">Confirm Password</Label>
                                            <div className="relative">
                                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                                                <Input
                                                    id="register-confirm-password"
                                                    type={showPassword ? 'text' : 'password'}
                                                    placeholder="Confirm your password"
                                                    value={registerConfirmPassword}
                                                    onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                                                    className="pl-10 h-12"
                                                    data-testid="register-confirm-password-input"
                                                />
                                            </div>
                                        </div>
                                        <Button
                                            type="submit"
                                            className="w-full h-12 bg-primary-600 hover:bg-primary-700"
                                            disabled={isSubmitting}
                                            data-testid="register-submit-btn"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                    Creating account...
                                                </>
                                            ) : (
                                                'Create Account'
                                            )}
                                        </Button>
                                    </form>

                                    <p className="text-xs text-center text-neutral-500">
                                        By creating an account, you agree to our{' '}
                                        <Link to="/terms" className="text-primary-600 hover:underline">Terms of Service</Link>
                                        {' '}and{' '}
                                        <Link to="/privacy" className="text-primary-600 hover:underline">Privacy Policy</Link>
                                    </p>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>

            {/* Right Panel - Hero */}
            <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary-950 via-primary-900 to-neutral-900 text-white p-12 items-center justify-center">
                <div className="max-w-lg">
                    <h2 className="font-heading text-4xl font-bold mb-6">
                        Find Your Perfect Job Match
                    </h2>
                    <p className="text-lg text-neutral-300 mb-8">
                        Connect with recruitment agencies near you and let our experts help you 
                        land your dream job faster.
                    </p>
                    <div className="space-y-4">
                        {[
                            'Access 4500+ verified recruitment agencies in UK & Canada',
                            'Human-assisted job applications',
                            'Track all your applications in one place',
                            'Expert career consultation',
                        ].map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                                <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <span className="text-neutral-200">{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <Toaster position="top-right" richColors />
        </div>
    );
};

export default LoginPage;
